import { Chip, type ChipTone } from '@/components/Chip/Chip';
import { DeleteButton } from '@/components/DeleteButton/DeleteButton';
import type { FileListRow } from '@/graphql/files';
import { formatBytes, shortId } from '@/lib/format';

import styles from './FileCard.module.css';

export interface FileCardProps {
  file: FileListRow;
  onOpen: (fileId: string) => void;
  /** Omitted, the card offers no delete at all. */
  onDelete?: (fileId: string) => void;
  deleting?: boolean;
  /** Deleted in this session. The row stays until the listing is refetched. */
  deleted?: boolean;
  deleteError?: string | null;
}

/**
 * Why a card and not a table row: the one question this view answers fastest
 * is "did this file productionize", and a thumbnail answers it before any
 * column does — a card with no image is a file the pipeline never drew.
 */
export function FileCard({
  file,
  onOpen,
  onDelete,
  deleting = false,
  deleted = false,
  deleteError = null,
}: FileCardProps) {
  const preview = file.preview[0];
  const job = file.canonicalization;

  return (
    <li className={`${styles.item} ${deleted ? styles.deletedItem : ''}`}>
      <div className={styles.card}>
        <button
          className={styles.open}
          disabled={deleted}
          onClick={() => onOpen(file.id)}
          type="button"
        >
          <span className={styles.thumb}>
            {preview ? (
              <img alt="" className={styles.image} loading="lazy" src={preview.downloadUrl} />
            ) : (
              <span className={styles.noThumb}>no preview</span>
            )}
          </span>

          <span className={styles.body}>
            <span className={styles.filename}>{file.filename ?? 'unnamed'}</span>
            <span className={styles.meta}>
              {file.contentType ?? 'unknown type'} · {formatBytes(file.sizeBytes)}
              {file.pageCount ? ` · ${file.pageCount}p` : ''}
            </span>
            <span className={styles.chips}>
              {deleted ? <Chip tone="bad">deleted</Chip> : null}
              <Chip tone={file.status === 'VALIDATED' ? 'good' : 'warn'}>
                {file.status ?? 'UNKNOWN'}
              </Chip>
              <Chip tone={canonicalTone(file)}>{canonicalLabel(file)}</Chip>
              {file.extAppApiId ? (
                <Chip label="app-api">{file.extAppApiId}</Chip>
              ) : (
                <Chip tone="warn">no app-api ref</Chip>
              )}
              {file.migratedAt ? <Chip>migrated</Chip> : null}
            </span>
            <span className={styles.id}>…{shortId(file.id)}</span>
            {job?.verdict && job.verdict !== 'ok' ? (
              <span className={styles.verdict}>{job.verdict}</span>
            ) : null}
          </span>
        </button>

        {/* Deletable only while no app-api record points at these bytes: an
            app-api reference means something over there holds this id, and a
            soft delete here would leave it resolving to nothing. Artifacts
            never reach this card at all — the listing asks for documents only
            — which is what keeps the rule above from singling out exactly the
            rows a delete must never touch. */}
        {onDelete && !file.extAppApiId && !deleted ? (
          <span className={styles.deleteSlot}>
            <DeleteButton
              busy={deleting}
              label={file.filename ?? 'this file'}
              onDelete={() => onDelete(file.id)}
              size="compact"
            />
          </span>
        ) : null}
      </div>

      {deleteError ? (
        <p className={styles.error} role="alert">
          {deleteError}
        </p>
      ) : null}
    </li>
  );
}

/**
 * The canonical and the job are two different facts and their disagreement is
 * the interesting one: a COMPLETE job with no canonical is a file Bindery
 * answered about and rejected, which no amount of waiting will fix.
 */
function canonicalLabel(file: FileListRow): string {
  if (file.canonicalFileId) {
    return 'canonical';
  }
  const status = file.canonicalization?.status;
  if (!status) {
    return 'no job';
  }
  return status === 'COMPLETE' ? 'COMPLETE, no canonical' : status;
}

function canonicalTone(file: FileListRow): ChipTone {
  if (file.canonicalFileId) {
    return 'good';
  }
  const status = file.canonicalization?.status;
  if (status === 'FAILED' || status === 'COMPLETE') {
    return 'bad';
  }
  return status ? 'warn' : 'neutral';
}
